import { cn } from "@/utils/utils";
import React from "react";

export type iconSize =
  | "2"
  | "xs"
  | "14"
  | "sm"
  | "18"
  | "5"
  | "22"
  | "md"
  | "7"
  | "lg"
  | "xl"
  | "2xl";

/**
 * Token name to the class it produces. The classes are written out in full
 * because Tailwind's scanner reads source text — an interpolated
 * `text-${color}` is invisible to it, so the class only ever reached the
 * stylesheet when some other file happened to write it, and six values in use
 * built a class nothing wrote.
 */
export const ICON_COLORS = {
  primary: "text-primary",
  secondary: "text-secondary",
  tertiary: "text-tertiary",
  cta: "text-cta",
  link: "text-link",
  error: "text-error",
  warning: "text-warning",
  positive: "text-positive",
  negative: "text-negative",
  natural: "text-natural",
  disabled: "text-disabled",
  h1: "text-h1",
  white: "text-on-surface",
  "on-primary": "text-on-primary",
  "text-body-primary": "text-text-body-primary",
  "text-body-secondary": "text-text-body-secondary",
  "charts-positive": "text-charts-positive",
  "charts-negative": "text-charts-negative",
  "border-field-default": "text-border-field-default",
  "border-border1": "text-border-border1",
  inherit: "text-inherit",
  /** Takes the colour from the parent, for icons a container deliberately tints. */
  current: "text-current"
} as const;

export type IconColor = keyof typeof ICON_COLORS;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  size?: iconSize;
  color?: IconColor;
  /**
   * The element to render. A `div` is not valid inside a `button`, whose
   * content model admits phrasing content only, so callers nesting an icon in
   * one pass `span`.
   */
  as?: "div" | "span";
  /** Keeps the icon's own stroke: skips the size's standard width and non-scaling stroke. */
  nativeStroke?: boolean;
}

const IconContainer = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      size = "sm",
      color,
      children,
      className = "",
      style,
      as: Element = "div",
      nativeStroke = false,
      ...divProps
    },
    ref
  ) => {
    const sizeClasses = {
      "2": "size-2",
      xs: "size-3",
      "14": "size-[14px]",
      sm: "size-4",
      "18": "size-[18px]",
      "5": "size-5",
      "22": "size-[22px]",
      md: "size-6",
      "7": "size-7",
      lg: "size-8",
      xl: "size-10",
      "2xl": "size-12"
    };

    // Normalize on-screen line weight regardless of each icon's viewBox.
    // `non-scaling-stroke` makes stroke-width measured in screen px (ignores the
    // viewBox→box scale), so these widths render consistently across all icons.
    const strokeClasses: Record<iconSize, string> = {
      "2": "[&_*]:[stroke-width:1px]",
      xs: "[&_*]:[stroke-width:1px]",
      "14": "[&_*]:[stroke-width:1px]",
      sm: "[&_*]:[stroke-width:1.2px]",
      "18": "[&_*]:[stroke-width:1.2px]",
      "5": "[&_*]:[stroke-width:1.4px]",
      "22": "[&_*]:[stroke-width:1.4px]",
      md: "[&_*]:[stroke-width:1.4px]",
      "7": "[&_*]:[stroke-width:1.6px]",
      lg: "[&_*]:[stroke-width:1.6px]",
      xl: "[&_*]:[stroke-width:2px]",
      "2xl": "[&_*]:[stroke-width:2.4px]"
    };

    const textColorClass = color ? ICON_COLORS[color] : undefined;

    return (
      <Element
        {...divProps}
        ref={ref}
        className={cn(
          sizeClasses[size],
          "flex justify-center items-center",
          !nativeStroke && "[&_*]:[vector-effect:non-scaling-stroke]",
          !nativeStroke && strokeClasses[size],
          textColorClass,
          className
        )}
        style={style}
      >
        {children}
      </Element>
    );
  }
);
IconContainer.displayName = "IconContainer";

export default IconContainer;
